import { defineStore } from 'pinia'
import { ref } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import type { Transaction, TransactionEntry, SplitRule, GrootboekRekening, BusinessRules, Relatie } from '@/types'
import { DEMO_TRANSACTIONS, DEMO_SPLIT_RULES, DEFAULT_ACCOUNTS, DEFAULT_BUSINESS_RULES, DEMO_RELATIES, DEFAULT_RELATIES } from '@/utils/dummyData'

export const useBoekhoudingStore = defineStore('boekhouding', () => {
  const accounts = ref<GrootboekRekening[]>(DEFAULT_ACCOUNTS)
  const relaties = ref<Relatie[]>(DEFAULT_RELATIES)
  const transactions = ref<Transaction[]>([])
  const splitRules = ref<SplitRule[]>([])
  const businessRules = ref<BusinessRules>(DEFAULT_BUSINESS_RULES)
  const sequences = ref<Record<string, number>>({})
  const fileHandle = ref<FileSystemFileHandle | null>(null)

  function nextBoekstuknr(datum: string): string {
    const year = datum.slice(0, 4)
    const nextSeq = (sequences.value[year] ?? 0) + 1
    return `${year}${String(nextSeq).padStart(3, '0')}`
  }

  function validateTransaction(transaction: Transaction): void {
    const sumDebet = transaction.entries.reduce((sum, e) => sum + e.debet, 0)
    const sumCredit = transaction.entries.reduce((sum, e) => sum + e.credit, 0)
    if (sumDebet !== sumCredit) {
      throw new Error(`Transaction is not balanced: SUM(debet) [${sumDebet}] !== SUM(credit) [${sumCredit}]`)
    }
  }

  function isLocked(datum: string): boolean {
    if (!businessRules.value.lockedUntil) return false
    return datum <= businessRules.value.lockedUntil
  }

  function addTransaction(data: Omit<Transaction, 'id' | 'boekstuknr' | 'createdAt' | 'updatedAt'>): Transaction {
    if (isLocked(data.datum)) {
      throw new Error(`Kan geen transactie toevoegen: de periode tot en met ${businessRules.value.lockedUntil} is vergrendeld.`)
    }

    const year = data.datum.slice(0, 4)
    const now = new Date().toISOString()
    const transaction: Transaction = {
      ...data,
      id: uuidv4(),
      boekstuknr: nextBoekstuknr(data.datum),
      createdAt: now,
      updatedAt: now,
    }

    validateTransaction(transaction)
    
    transactions.value.push(transaction)
    
    // Increment sequence
    sequences.value[year] = (sequences.value[year] ?? 0) + 1
    
    return transaction
  }

  function updateTransaction(id: string, data: Partial<Omit<Transaction, 'id' | 'boekstuknr' | 'createdAt' | 'updatedAt'>>): void {
    const index = transactions.value.findIndex((t) => t.id === id)
    if (index !== -1) {
      const original = transactions.value[index]!
      
      // Check if original date is locked
      if (isLocked(original.datum)) {
        throw new Error(`Kan transactie niet wijzigen: de oorspronkelijke datum (${original.datum}) valt in een vergrendelde periode.`)
      }
      
      // Check if new date is locked
      if (data.datum && isLocked(data.datum)) {
        throw new Error(`Kan transactie niet verplaatsen naar ${data.datum}: deze datum valt in een vergrendelde periode.`)
      }

      const updated = { 
        ...original, 
        ...data,
        updatedAt: new Date().toISOString()
      } as Transaction
      
      validateTransaction(updated)
      transactions.value[index] = updated
    }
  }

  function deleteTransaction(id: string): void {
    const index = transactions.value.findIndex((t) => t.id === id)
    if (index !== -1) {
      const transaction = transactions.value[index]!
      if (isLocked(transaction.datum)) {
        throw new Error(`Kan transactie niet verwijderen: de datum (${transaction.datum}) valt in een vergrendelde periode.`)
      }
      transactions.value.splice(index, 1)
    }
  }

  function stornoTransaction(id: string): Transaction {
    const original = transactions.value.find(t => t.id === id)
    if (!original) throw new Error('Original transaction not found')

    if (isLocked(original.datum)) {
      throw new Error(`Kan storno niet uitvoeren: de oorspronkelijke transactie valt in een vergrendelde periode.`)
    }

    const now = new Date().toISOString()
    const stornoEntries: TransactionEntry[] = original.entries.map(e => ({
      ...e,
      id: uuidv4(),
      debet: e.credit, // Swap debet/credit
      credit: e.debet, // Swap debet/credit
    }))

    const storno: Transaction = {
      id: uuidv4(),
      boekstuknr: nextBoekstuknr(original.datum),
      datum: original.datum,
      omschrijving: `STORNO: ${original.omschrijving}`,
      entries: stornoEntries,
      createdAt: now,
      updatedAt: now,
      stornoForId: original.id
    }

    transactions.value.push(storno)
    
    // Increment sequence
    const year = original.datum.slice(0, 4)
    sequences.value[year] = (sequences.value[year] ?? 0) + 1

    return storno
  }

  function addRelatie(data: Omit<Relatie, 'id'>): Relatie {
    const relatie: Relatie = {
      ...data,
      id: uuidv4(),
    }
    relaties.value.push(relatie)
    return relatie
  }

  function updateRelatie(id: string, data: Partial<Omit<Relatie, 'id'>>): void {
    const index = relaties.value.findIndex((r) => r.id === id)
    if (index !== -1) {
      relaties.value[index] = { ...relaties.value[index], ...data } as Relatie
    }
  }

  function deleteRelatie(id: string): void {
    const index = relaties.value.findIndex((r) => r.id === id)
    if (index !== -1) {
      relaties.value.splice(index, 1)
    }
  }

  function addSplitRule(data: Omit<SplitRule, 'id'>): SplitRule {
    const rule: SplitRule = {
      ...data,
      id: uuidv4(),
    }
    splitRules.value.push(rule)
    return rule
  }

  function updateSplitRule(id: string, data: Partial<Omit<SplitRule, 'id'>>): void {
    const index = splitRules.value.findIndex((r) => r.id === id)
    if (index !== -1) {
      splitRules.value[index] = { ...splitRules.value[index], ...data } as SplitRule
    }
  }

  function deleteSplitRule(id: string): void {
    const index = splitRules.value.findIndex((r) => r.id === id)
    if (index !== -1) {
      splitRules.value.splice(index, 1)
    }
  }

  function updateBusinessRules(data: Partial<BusinessRules>): void {
    businessRules.value = { ...businessRules.value, ...data }
  }

  function recalculateSequences(): void {
    const newSeqs: Record<string, number> = {}
    for (const t of transactions.value) {
      const year = t.boekstuknr.slice(0, 4)
      const seq = parseInt(t.boekstuknr.slice(4), 10)
      if (!newSeqs[year] || seq > newSeqs[year]) {
        newSeqs[year] = seq
      }
    }
    sequences.value = newSeqs
  }

  function setData(newTransactions: Transaction[], newSplitRules: SplitRule[], newAccounts?: GrootboekRekening[], newBusinessRules?: BusinessRules, newRelaties?: Relatie[], newSequences?: Record<string, number>): void {
    transactions.value = newTransactions
    splitRules.value = newSplitRules
    if (newAccounts) accounts.value = newAccounts
    if (newBusinessRules) businessRules.value = newBusinessRules
    if (newRelaties) relaties.value = newRelaties
    if (newSequences) {
      sequences.value = newSequences
    } else {
      recalculateSequences()
    }
  }

  function loadDemoData(): void {
    accounts.value = JSON.parse(JSON.stringify(DEFAULT_ACCOUNTS))
    relaties.value = JSON.parse(JSON.stringify(DEMO_RELATIES))
    transactions.value = JSON.parse(JSON.stringify(DEMO_TRANSACTIONS))
    splitRules.value = JSON.parse(JSON.stringify(DEMO_SPLIT_RULES))
    businessRules.value = JSON.parse(JSON.stringify(DEFAULT_BUSINESS_RULES))
    recalculateSequences()
  }

  return {
    accounts,
    relaties,
    transactions,
    splitRules,
    businessRules,
    sequences,
    fileHandle,
    nextBoekstuknr,
    isLocked,
    addTransaction,
    updateTransaction,
    deleteTransaction,
    stornoTransaction,
    addRelatie,
    updateRelatie,
    deleteRelatie,
    addSplitRule,
    updateSplitRule,
    deleteSplitRule,
    updateBusinessRules,
    setData,
    loadDemoData,
  }
})
